import zenPerformance from '@zenlayer/zen-performance';
import { useRequest, useUnmount, useUpdateEffect } from 'ahooks';
import qs from 'query-string';
import { keys, omit } from 'ramda';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import notification from '../notification';
import { DEFAULT_pageNum, DEFAULT_pageSize, DEFAULT_pageSizeOptions } from '../pagination/pagination';
export { DEFAULT_pageSize };
export const DEFAULT_queryKey = 'query';
function isEmptyObject(obj) {
  return Object.keys(obj).length === 0;
}

// 返回url上存储的list查询参数
export const listUrlParamsParse = function (search) {
  let queryKey = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_queryKey;
  const searchObj = qs.parse(search);
  const queryObj = qs.parse(searchObj === null || searchObj === void 0 ? void 0 : searchObj[queryKey], {
    arrayFormat: 'bracket'
  });
  queryObj.pageSize = typeof queryObj.pageSize === 'string' && DEFAULT_pageSizeOptions.includes(queryObj.pageSize) ? Number(queryObj.pageSize) : DEFAULT_pageSize;
  queryObj.pageNum = Number(queryObj.pageNum) > 0 ? Number(queryObj.pageNum) : DEFAULT_pageNum;
  return queryObj;
};

//返回url的search字符串，如果想返回完整search参数，请一并传otherSearch，否则只返回list参数的search值
export const listUrlParamsStringify = function (params) {
  let otherSearch = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let queryKey = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : DEFAULT_queryKey;
  return qs.stringify({
    ...otherSearch,
    [queryKey]: isEmptyObject(params) ? undefined : qs.stringify(params, {
      arrayFormat: 'bracket'
    })
  });
};
export function useListRequest(asyncApi, opts) {
  var _FetchRequest$data;
  const location = useLocation();
  const search = qs.parse(location.search.replace(/^\?+/, '')); // 只取reload用，移除开头的?解决详情跳转url出现双?的问题

  const productListPerformance = new zenPerformance.observer.productList(location.pathname);
  const {
    defaultParams,
    params2search,
    manual,
    queryKey = DEFAULT_queryKey,
    onSuccess: _onSuccess,
    handlePollingInterval,
    pollingWhenHidden = false,
    ...otherOpts
  } = opts;
  const [paramsState, setParamsState] = useState(defaultParams);
  const [pollingInterval, setPollingInterval] = useState(0);
  const pollingIntervalTimer = useRef();
  const onSuccess = useCallback((data, params) => {
    _onSuccess === null || _onSuccess === void 0 || _onSuccess(data);
    if (!(data !== null && data !== void 0 && data.success)) {
      notification.error({
        description: data.msg
      });
    }
    if (handlePollingInterval) {
      const newPollingInterval = handlePollingInterval(data);
      if (newPollingInterval === false || typeof newPollingInterval === 'number' && newPollingInterval <= 0 || pollingIntervalTimer.current === false) {
        if (pollingInterval !== 0) {
          setPollingInterval(0);
        }
      } else if (newPollingInterval === true) {
        if (pollingInterval !== 10000) {
          setPollingInterval(10000);
        }
        // https://github.com/alibaba/hooks/issues/1827
        if (pollingInterval === 0) {
          clearTimeout(pollingIntervalTimer.current);
          pollingIntervalTimer.current = setTimeout(() => {
            FetchRequest.refresh();
          }, 10000);
        }
      } else if (newPollingInterval > 0) {
        if (pollingInterval !== newPollingInterval) {
          setPollingInterval(newPollingInterval);
        }
        if (pollingInterval === 0) {
          clearTimeout(pollingIntervalTimer.current);
          pollingIntervalTimer.current = setTimeout(() => {
            FetchRequest.refresh();
          }, newPollingInterval);
        }
      }
    }
  }, [pollingInterval, pollingIntervalTimer.current]);
  const [initing, setIniting] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const onBefore = useCallback(() => {
    if (params2search) {
      productListPerformance.productListStart();
    }
  }, []);
  const onFinally = useCallback((params, data) => {
    if (params2search) {
      productListPerformance.productListEnd();
    }
    if (initing === true) {
      setIniting(false);
      if (!keys(omit(['pageNum', 'pageSize', 'total'], paramsState)).length && paramsState.pageNum === 1 && !(data !== null && data !== void 0 && data.data.total)) {
        //1.说明除了pageNum、pageSize外没有其余请求参数
        //2.总数为0
        setIsEmpty(true);
      } else {
        setIsEmpty(false);
      }
    } else if (!!(data !== null && data !== void 0 && data.data.total)) {
      setIsEmpty(false);
    }
  }, [paramsState, initing]);
  const FetchRequest = useRequest(p => {
    if (params2search) {
      const newUrlSearchStr = "?".concat(listUrlParamsStringify(p, search, queryKey));
      window.history.replaceState({}, '', window.location.pathname + newUrlSearchStr);

      // history.replace({
      //   search: listUrlParamsStringify(p, search, queryKey),
      // });
    }
    setParamsState(p);
    return asyncApi(p);
  }, {
    manual: true,
    onSuccess,
    onBefore,
    onFinally,
    pollingInterval,
    pollingWhenHidden,
    ...otherOpts
  });
  const run = params => {
    setPollingInterval(0);
    FetchRequest.run(params);
  };
  const runAsync = async params => {
    setPollingInterval(0);
    return FetchRequest.runAsync(params);
  };
  const refresh = () => {
    setPollingInterval(0);
    FetchRequest.refresh();
  };
  const refreshAsync = async () => {
    setPollingInterval(0);
    return await FetchRequest.refreshAsync();
  };
  useEffect(() => {
    if (!manual) {
      run(paramsState);
    }
  }, []);
  useUnmount(() => {
    FetchRequest.cancel();
    clearTimeout(pollingIntervalTimer.current);
    pollingIntervalTimer.current = false;
  });
  useUpdateEffect(() => {
    if (search.reload) {
      run(defaultParams); // reload用默认参数刷新
    }
  }, [JSON.stringify(search.reload)]);
  return {
    ...FetchRequest,
    paramsState,
    run,
    runAsync,
    refresh,
    refreshAsync,
    initing,
    isEmpty,
    loading: !pollingInterval && FetchRequest.loading,
    //轮训时不展示loading
    pollingLoading: !!pollingInterval && FetchRequest.loading,
    //轮训时展示的loading
    pagination: {
      onChange: (pageNum, pageSize) => {
        run({
          ...paramsState,
          pageNum,
          pageSize
        });
      },
      pageNum: Number(paramsState === null || paramsState === void 0 ? void 0 : paramsState.pageNum) || 1,
      total: Number(FetchRequest === null || FetchRequest === void 0 || (_FetchRequest$data = FetchRequest.data) === null || _FetchRequest$data === void 0 || (_FetchRequest$data = _FetchRequest$data.data) === null || _FetchRequest$data === void 0 ? void 0 : _FetchRequest$data.total) || 0,
      pageSize: Number(paramsState === null || paramsState === void 0 ? void 0 : paramsState.pageSize) || DEFAULT_pageSize
    }
  };
}